import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { useMemo } from 'react';
import { format, subDays } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface ChartDataPoint {
  date: string;
  totalTransactions: number;
  trxTransfers: number;
  usdtTransfers: number;
}

// Seeded random number generator based on date string
const seededRandom = (seed: string): () => number => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  return () => {
    hash = (hash * 9301 + 49297) % 233280;
    return hash / 233280;
  };
};

const TransactionChart = () => {
  const { t } = useTranslation();
  
  // Generate chart data based on current date - consistent within the same day
  const chartData = useMemo<ChartDataPoint[]>(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const data: ChartDataPoint[] = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = subDays(new Date(), i);
      const dateStr = format(date, 'yyyy-MM-dd');
      const random = seededRandom(dateStr + '-tronscope-chart');
      
      // Generate consistent values for this specific date
      const baseTransactions = 8 + random() * 7; // 8-15 million range
      
      data.push({
        date: format(date, 'MM-dd'),
        totalTransactions: Math.round(baseTransactions * 10) / 10,
        trxTransfers: Math.round((baseTransactions * (0.55 + random() * 0.15)) * 10) / 10,
        usdtTransfers: Math.round((baseTransactions * (0.2 + random() * 0.15)) * 10) / 10,
      });
    }
    return data;
  }, []);

  return (
    <div className="glass-card p-3 sm:p-6 overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
        <h3 className="font-semibold text-foreground text-sm sm:text-base">{t('chart.transactions')}</h3>
        <div className="flex items-center gap-3 sm:gap-4 text-[10px] sm:text-sm flex-wrap">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-primary" />
            <span className="text-muted-foreground">{t('chart.total')}</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-success" />
            <span className="text-muted-foreground">{t('chart.trx')}</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-warning" />
            <span className="text-muted-foreground">{t('chart.usdt')}</span>
          </div>
        </div>
      </div>

      <div className="h-[160px] sm:h-[200px] -ml-2 sm:ml-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <XAxis 
              dataKey="date" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
              tickFormatter={(value) => `${value}m`}
              width={30}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px',
              }}
              labelStyle={{ color: 'hsl(var(--foreground))' }}
              formatter={(value: number) => [`${value}M`, '']}
            />
            <Line
              type="monotone"
              dataKey="totalTransactions"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="trxTransfers"
              stroke="hsl(var(--success))"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="usdtTransfers"
              stroke="hsl(var(--warning))"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TransactionChart;
